import {useEffect, useMemo} from 'react'
import {useParams} from 'react-router-dom'

import {useQueryParamState} from '../../../../hooks/routes'
import type {GetBuildApiArg} from '../../../../services/rest'
import {restApi} from '../../../../services/rest'
import {subscribeOnBuildTypeEvents, subscribeOnProjectEvents} from '../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../utils/subscriptionEvents'
import {usePipelineId} from '../hooks/pipeline'
import {pipelinesApi} from '../services/pipelinesApi'

export const usePipelineRunArg = (): GetBuildApiArg => {
  const {runId = ''} = useParams()
  return useMemo(
    () => ({
      buildLocator: `id:${runId}`,
      fields:
        'id,number,state,branchName,defaultBranch,status,statusText,canceledInfo' +
        ',failedToStart,personal,detachedFromAgent,waitReason,startEstimate,user(id)' +
        ',triggered(user(username,name,avatars(urlToSize20,urlToSize40)),displayText,date)' +
        ',queuedDate,startDate,finishDate,buildType(id,internalId,project(id))' +
        ',running-info(percentageComplete,elapsedSeconds,estimatedTotalSeconds,leftSeconds,overtime)' +
        ',snapshot-dependencies(build(' +
        'id,state,status,statusText,number,canceledInfo,failedToStart,personal,detachedFromAgent' +
        ',parallelized,waitReason,startEstimate,startDate,finishDate,user(id),buildType(name,project(internalId))' +
        ',running-info(percentageComplete,leftSeconds)' +
        ',snapshot-dependencies(buildType:(project:(virtual:true)),build(id,running-info(percentageComplete)))' +
        '))',
    }),
    [runId],
  )
}

export const usePipelineRunPage = () => {
  const pipelineId = usePipelineId()
  const arg = usePipelineRunArg()
  const {
    buildLoading,
    dependencies = [],
    buildError,
    headInternalId,
    refetch,
  } = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data, isLoading, error}) => {
      const run = data?.entities?.builds?.[data?.result!]
      const headId = run?.buildType
      const runSnapshotDependenciesBuild = run?.['snapshot-dependencies']?.build
      return {
        buildLoading: isLoading,
        dependencies: runSnapshotDependenciesBuild,
        buildError: error,
        headInternalId: data?.entities?.buildTypes?.[headId!]?.internalId,
      }
    },
  })
  const {pipelineLoading, pipelineError} = pipelinesApi.endpoints.getPipelineById.useQuery(
    pipelineId,
    {
      selectFromResult: ({isLoading, error}) => ({
        pipelineLoading: isLoading,
        pipelineError: error,
      }),
    },
  )
  const isOnlyOneJob = dependencies.length === 1
  const [firstDependency] = dependencies
  const [jobId, setJobId] = useQueryParamState('job')
  useEffect(() => {
    if (isOnlyOneJob && !jobId) {
      setJobId(String(firstDependency?.id))
    }
  }, [firstDependency?.id, isOnlyOneJob, jobId, setJobId])
  const childInternalId = firstDependency?.buildType?.project?.internalId

  useEffect(
    () =>
      childInternalId
        ? subscribeOnProjectEvents(
            childInternalId,
            [BUILD_TYPE_REMOVED_FROM_QUEUE, BUILD_STARTED, BUILD_FINISHED, BUILD_INTERRUPTED],
            refetch,
          )
        : undefined,
    [childInternalId, refetch],
  )

  useEffect(
    () =>
      headInternalId
        ? subscribeOnBuildTypeEvents(
            headInternalId,
            [BUILD_TYPE_REMOVED_FROM_QUEUE, BUILD_STARTED, BUILD_FINISHED, BUILD_INTERRUPTED],
            refetch,
          )
        : undefined,
    [headInternalId, refetch],
  )

  return {
    jobId,
    buildError,
    pipelineError,
    buildLoading,
    pipelineLoading,
  }
}
